import type { AlertFact } from '@/api/types'
import { useCurrentTenant } from '@/lib/access'
import { comparisonBasisLabel, formatFactValue } from '@/lib/insights/factFormat'

import { Disclosure } from './Disclosure'
import { FactRow } from './FactRow'

/**
 * The measured facts an alert was raised on, behind a disclosure. Alerts are a
 * report surface, so these are measurements only (ADR-0009 D9.11.1): the
 * client never hands this an estimate.
 */
export function AlertFacts({ facts }: { facts: readonly AlertFact[] }) {
  const tenant = useCurrentTenant().data
  if (facts.length === 0) return null
  const reporting = { currency: tenant?.currency ?? null, timeZone: tenant?.timezone ?? null }

  return (
    <Disclosure
      showLabel={`Show the figures (${facts.length})`}
      hideLabel="Hide the figures"
      listLabel="Figures behind this alert"
    >
      {facts.map((fact) => (
        <FactRow
          key={fact.key}
          kind={fact.kind}
          name={fact.entityName}
          basis="measured"
          value={formatFactValue(fact.value, fact, reporting)}
          comparison={
            fact.comparison
              ? `${formatFactValue(fact.comparison.value, fact, reporting)} (${comparisonBasisLabel(fact.comparison.basis)})`
              : null
          }
          periodStart={fact.period.start}
          periodEnd={fact.period.end}
          confidence={fact.confidence}
        />
      ))}
    </Disclosure>
  )
}
